import { useState, useEffect } from "react";
import { api, courseImageUrl, type AuthUser, type CourseResource } from "@/lib/api";

const emptyCourseForm = {
  name: "",
  description: "",
  detail_title: "",
  modality: "Presencial",
  target_audience: "",
  highlights_text: "",
  resources_text: "",
  duration: "",
  hours: "",
  category: "Seguridad Industrial",
  image_url: "",
  show_on_landing: true,
  active: true,
};

function parseLines(value: string) {
  return value.split("\n").map((line) => line.trim()).filter(Boolean);
}

function parseResources(value: string): CourseResource[] {
  return parseLines(value).map((line) => {
    const [rawName, ...rest] = line.split("|");
    const url = (rest.join("|") || rawName).trim();
    const fallbackName = url.split("/").pop() || "Recurso del curso";
    return { name: rest.length ? rawName.trim() || fallbackName : fallbackName, url };
  });
}

function formatResources(resources?: CourseResource[]) {
  return (resources || []).map((resource) => `${resource.name} | ${resource.url}`).join("\n");
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function NuevoCursoModal({ isOpen, onClose, onCreated }: Props) {
  const [form, setForm] = useState(emptyCourseForm);
  const [image, setImage] = useState<File | null>(null);
  const [resources, setResources] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const course = await api.createCourse({
        name: form.name,
        description: form.description,
        detail_title: form.detail_title,
        modality: form.modality,
        target_audience: form.target_audience,
        highlights: parseLines(form.highlights_text),
        resource_files: parseResources(form.resources_text),
        duration: form.duration,
        hours: parseInt(form.hours, 10) || 0,
        category: form.category,
        image_url: form.image_url,
        show_on_landing: form.show_on_landing,
        active: form.active,
      });
      if (image) await api.uploadCourseImage(course.id, image);
      for (const file of resources) {
        await api.uploadCourseResource(course.id, file);
      }
      onCreated();
      onClose();
      setForm(emptyCourseForm);
      setImage(null);
      setResources([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear curso");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]">Nuevo curso (catálogo)</h2>
          <button onClick={onClose} className="cursor-pointer"><i className="ri-close-line text-xl"></i></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Nombre del curso *</label>
            <input className="input-field" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Descripción detallada *</label>
            <textarea className="input-field min-h-[80px]" placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Título público del detalle</label>
            <input className="input-field" placeholder="Ej. Curso certificado de seguridad H2S" value={form.detail_title} onChange={(e) => setForm({ ...form, detail_title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Modalidad</label>
              <select className="input-field" value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}>
                <option value="Presencial">Presencial</option>
                <option value="Online">Online</option>
                <option value="Híbrido">Híbrido</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Dirigido a</label>
              <input className="input-field" placeholder="Operadores, supervisores, brigadas..." value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Puntos importantes / destacados</label>
            <textarea className="input-field min-h-[90px]" placeholder="Uno por línea" value={form.highlights_text} onChange={(e) => setForm({ ...form, highlights_text: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Duración (texto) *</label>
              <input className="input-field" placeholder="Ej. 2 días" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Horas totales *</label>
              <input type="number" className="input-field" placeholder="Ej. 16" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Categoría / Industria</label>
            <input className="input-field" placeholder="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
          <div className="space-y-3">
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Imagen del curso</label>
            <input className="input-field" placeholder="URL de la imagen (Opcional si subes archivo)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            <div className="flex items-center gap-4">
              <span className="text-xs text-[var(--color-text-muted)]">O sube un archivo local:</span>
              <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
            </div>
          </div>
          <div className="space-y-3">
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Recursos descargables</label>
            <textarea className="input-field min-h-[80px]" placeholder="Nombre del recurso | https://..." value={form.resources_text} onChange={(e) => setForm({ ...form, resources_text: e.target.value })} />
            <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.zip" onChange={(e) => setResources(Array.from(e.target.files || []))} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.show_on_landing} onChange={(e) => setForm({ ...form, show_on_landing: e.target.checked })} />
            Mostrar en landing page
          </label>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary cursor-pointer">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary cursor-pointer">{loading ? "Guardando..." : "Crear curso"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditionProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  onCreated: () => void;
}

export function NuevaEdicionModal({ isOpen, onClose, courseId, onCreated }: EditionProps) {
  const [instructors, setInstructors] = useState<AuthUser[]>([]);
  const [form, setForm] = useState({
    edition_label: "", start_date: "", end_date: "", location: "",
    instructor_id: "", evaluator_id: "", capacity: "20", price: "", status: "programado",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) api.getInstructors().then(setInstructors).catch(() => {});
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.createEdition({
        course_id: courseId,
        edition_label: form.edition_label,
        start_date: form.start_date,
        end_date: form.end_date,
        location: form.location,
        instructor_id: form.instructor_id || null,
        evaluator_id: form.evaluator_id || null,
        capacity: parseInt(form.capacity, 10),
        price: parseFloat(form.price) || 0,
        status: form.status,
      });
      onCreated();
      onClose();
      setForm({
        edition_label: "", start_date: "", end_date: "", location: "",
        instructor_id: "", evaluator_id: "", capacity: "20", price: "", status: "programado",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const staff = instructors.filter((u) => u.role === "instructor" || u.role === "admin" || u.role === "super_admin");
  const evaluators = instructors.filter((u) => u.role === "evaluador" || u.can_evaluate);

  return (
    <EditionModalShell title="Nueva edición (fecha / sede)" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Etiqueta de la edición (ej. Jul 2026 - Villahermosa)</label>
          <input className="input-field" placeholder="Etiqueta" value={form.edition_label} onChange={(e) => setForm({ ...form, edition_label: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Inicio *</label>
            <input type="date" className="input-field" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Fin *</label>
            <input type="date" className="input-field" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Ubicación / Sede *</label>
          <input className="input-field" placeholder="Ubicación *" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Instructor asignado</label>
            <select className="input-field" value={form.instructor_id} onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}>
              <option value="">(Sin instructor)</option>
              {staff.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Evaluador asignado</label>
            <select className="input-field" value={form.evaluator_id} onChange={(e) => setForm({ ...form, evaluator_id: e.target.value })}>
              <option value="">(Sin evaluador)</option>
              {evaluators.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Capacidad máxima</label>
            <input type="number" className="input-field" placeholder="Ej. 20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Precio (USD) *</label>
            <input type="number" className="input-field" placeholder="Precio" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Estado</label>
            <select className="input-field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="programado">Programado</option>
              <option value="en_curso">En curso</option>
              <option value="completado">Completado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <ModalActions onClose={onClose} loading={loading} submitLabel="Crear edición" />
      </form>
    </EditionModalShell>
  );
}

interface EditCourseProps {
  isOpen: boolean;
  onClose: () => void;
  course: import("@/lib/api").Course;
  onSaved: () => void;
}

export function EditarCursoModal({ isOpen, onClose, course, onSaved }: EditCourseProps) {
  const [form, setForm] = useState({
    name: course.name,
    description: course.description,
    detail_title: course.detail_title || "",
    modality: course.modality || "Presencial",
    target_audience: course.target_audience || "",
    highlights_text: (course.highlights || []).join("\n"),
    resources_text: formatResources(course.resource_files),
    duration: course.duration,
    hours: String(course.hours),
    category: course.category,
    image_url: course.image_url || "",
    show_on_landing: course.show_on_landing,
    active: course.active,
  });
  const [image, setImage] = useState<File | null>(null);
  const [resources, setResources] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: course.name,
        description: course.description,
        detail_title: course.detail_title || "",
        modality: course.modality || "Presencial",
        target_audience: course.target_audience || "",
        highlights_text: (course.highlights || []).join("\n"),
        resources_text: formatResources(course.resource_files),
        duration: course.duration,
        hours: String(course.hours),
        category: course.category,
        image_url: course.image_url || "",
        show_on_landing: course.show_on_landing,
        active: course.active,
      });
      setImage(null);
      setResources([]);
      setError("");
    }
  }, [isOpen, course]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.updateCourse(course.id, {
        name: form.name,
        description: form.description,
        detail_title: form.detail_title,
        modality: form.modality,
        target_audience: form.target_audience,
        highlights: parseLines(form.highlights_text),
        resource_files: parseResources(form.resources_text),
        duration: form.duration,
        hours: parseInt(form.hours, 10) || 0,
        category: form.category,
        image_url: form.image_url,
        show_on_landing: form.show_on_landing,
        active: form.active,
      });
      if (image) await api.uploadCourseImage(course.id, image);
      for (const file of resources) {
        await api.uploadCourseResource(course.id, file);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h2 className="text-lg font-bold">Editar curso</h2>
          <button onClick={onClose} className="cursor-pointer"><i className="ri-close-line text-xl"></i></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Nombre del curso *</label>
            <input className="input-field" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Descripción detallada *</label>
            <textarea className="input-field min-h-[80px]" placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Título público del detalle</label>
            <input className="input-field" placeholder="Ej. Curso certificado de seguridad H2S" value={form.detail_title} onChange={(e) => setForm({ ...form, detail_title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Modalidad</label>
              <select className="input-field" value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}>
                <option value="Presencial">Presencial</option>
                <option value="Online">Online</option>
                <option value="Híbrido">Híbrido</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Dirigido a</label>
              <input className="input-field" placeholder="Operadores, supervisores, brigadas..." value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Puntos importantes / destacados</label>
            <textarea className="input-field min-h-[90px]" placeholder="Uno por línea" value={form.highlights_text} onChange={(e) => setForm({ ...form, highlights_text: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Duración (texto) *</label>
              <input className="input-field" placeholder="Ej. 2 días" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Horas totales *</label>
              <input type="number" className="input-field" placeholder="Ej. 16" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Categoría / Industria</label>
            <input className="input-field" placeholder="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
          <div className="space-y-3">
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Imagen del curso</label>
            {form.image_url && (
              <img src={courseImageUrl(form.image_url)} alt="Preview" className="w-20 h-16 object-cover rounded-lg border border-[var(--color-border)] mb-2" />
            )}
            <input className="input-field" placeholder="URL de la imagen" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            <div className="flex items-center gap-4">
              <span className="text-xs text-[var(--color-text-muted)]">O reemplazar con archivo:</span>
              <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
            </div>
          </div>
          <div className="space-y-3">
            <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Recursos descargables</label>
            <textarea className="input-field min-h-[80px]" placeholder="Nombre del recurso | https://..." value={form.resources_text} onChange={(e) => setForm({ ...form, resources_text: e.target.value })} />
            <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.zip" onChange={(e) => setResources(Array.from(e.target.files || []))} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.show_on_landing} onChange={(e) => setForm({ ...form, show_on_landing: e.target.checked })} />
            Mostrar en landing page
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Curso activo
          </label>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <ModalActions onClose={onClose} loading={loading} submitLabel="Guardar cambios" />
        </form>
      </div>
    </div>
  );
}

interface EditEditionProps {
  isOpen: boolean;
  onClose: () => void;
  edition: import("@/lib/api").CourseEdition;
  onSaved: () => void;
}

export function EditarEdicionModal({ isOpen, onClose, edition, onSaved }: EditEditionProps) {
  const [instructors, setInstructors] = useState<AuthUser[]>([]);
  const [form, setForm] = useState({
    edition_label: edition.edition_label,
    start_date: edition.start_date.slice(0, 10),
    end_date: edition.end_date.slice(0, 10),
    location: edition.location,
    instructor_id: edition.instructor_id || "",
    evaluator_id: edition.evaluator_id || "",
    capacity: String(edition.capacity),
    price: String(edition.price),
    status: edition.status,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) api.getInstructors().then(setInstructors).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm({
        edition_label: edition.edition_label,
        start_date: edition.start_date.slice(0, 10),
        end_date: edition.end_date.slice(0, 10),
        location: edition.location,
        instructor_id: edition.instructor_id || "",
        evaluator_id: edition.evaluator_id || "",
        capacity: String(edition.capacity),
        price: String(edition.price),
        status: edition.status,
      });
      setError("");
    }
  }, [isOpen, edition]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.updateEdition(edition.id, {
        edition_label: form.edition_label,
        start_date: form.start_date,
        end_date: form.end_date,
        location: form.location,
        instructor_id: form.instructor_id || null,
        evaluator_id: form.evaluator_id || null,
        capacity: parseInt(form.capacity, 10),
        price: parseFloat(form.price) || 0,
        status: form.status,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const staff = instructors.filter((u) => u.role === "instructor" || u.role === "admin" || u.role === "super_admin");
  const evaluators = instructors.filter((u) => u.role === "evaluador" || u.can_evaluate);

  return (
    <EditionModalShell title="Editar edición" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Etiqueta de la edición</label>
          <input className="input-field" placeholder="Etiqueta" value={form.edition_label} onChange={(e) => setForm({ ...form, edition_label: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Inicio *</label>
            <input type="date" className="input-field" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Fin *</label>
            <input type="date" className="input-field" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Ubicación / Sede *</label>
          <input className="input-field" placeholder="Ubicación *" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Instructor asignado</label>
            <select className="input-field" value={form.instructor_id} onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}>
              <option value="">(Sin instructor)</option>
              {staff.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Evaluador asignado</label>
            <select className="input-field" value={form.evaluator_id} onChange={(e) => setForm({ ...form, evaluator_id: e.target.value })}>
              <option value="">(Sin evaluador)</option>
              {evaluators.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Capacidad máxima</label>
            <input type="number" className="input-field" placeholder="Ej. 20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Precio (USD) *</label>
            <input type="number" className="input-field" placeholder="Precio USD" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Estado</label>
            <select className="input-field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="programado">Programado</option>
              <option value="en_curso">En curso</option>
              <option value="completado">Completado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <ModalActions onClose={onClose} loading={loading} submitLabel="Guardar edición" />
      </form>
    </EditionModalShell>
  );
}

function EditionModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="cursor-pointer"><i className="ri-close-line text-xl"></i></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onClose, loading, submitLabel }: { onClose: () => void; loading: boolean; submitLabel: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="btn-secondary cursor-pointer">Cancelar</button>
      <button type="submit" disabled={loading} className="btn-primary cursor-pointer">{loading ? "Guardando..." : submitLabel}</button>
    </div>
  );
}
